import { useCallback, useState } from 'react';

import { useInFlightGuard } from './useInFlightGuard';

/**
 * The busy+error+guard envelope shared by the app's async submit handlers: an
 * in-flight guard (drops a same-frame double-submit, see useInFlightGuard), a
 * `busy` flag toggled around the call, and an `error` set from a thrown error.
 * `run(fn)` guards, sets busy, clears error, awaits `fn`, and on a throw stores
 * `err.message`; `finally` always clears busy. `setError` is exposed so a caller
 * can surface a pre-submit validation error before ever calling `run`.
 *
 * For handlers that diverge — a returned (not thrown) error, a bespoke catch
 * message, a tri-state result, a rethrow, or no busy flag — use useInFlightGuard
 * directly instead of bending this hook.
 */
export function useAsyncAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runExclusive = useInFlightGuard();
  const run = useCallback(
    (fn: () => Promise<void>): Promise<void | undefined> =>
      runExclusive(async () => {
        setBusy(true);
        setError(null);
        try {
          await fn();
        } catch (e) {
          setError((e as Error).message);
        } finally {
          setBusy(false);
        }
      }),
    [runExclusive],
  );
  return { busy, error, setError, run };
}
